import { useTranslation } from 'react-i18next';
import { useStore } from '../../store/store';
import CascaderCustom from '../../utils/CascaderCustom';
import { isATableBox } from '../../utils/boxes';
import { getSectionRecursiveMeta } from '../../utils/recursiveSections';
import logo from '../../images/symbol.svg'

// Tables to hide inside a recursive section — tables whose zone-grouping is
// already handled by the loop itself (not applicable to zone/zone_cleaning, which
// explicitly iterate per zone and WANT their per-zone table variants).
const RECURSIVE_MODEL_EXCLUDED_TABLE_PATTERNS = {
  prestation:          (name) => /PRESTATIONS.*ZONES|ALL_ARTICLES.*ZONES/i.test(name),
  prestation_cleaning: (name) => /PRESTATIONS.*ZONES|ALL_ARTICLES.*ZONES/i.test(name),
  materiel:            (name) => /MATERIALS.*ZONES|ALL_ARTICLES.*ZONES/i.test(name),
  materiel_security:   (name) => /MATERIALS.*ZONES|ALL_ARTICLES.*ZONES/i.test(name),
  material_cleaning:   (name) => /MATERIALS.*ZONES|ALL_ARTICLES.*ZONES/i.test(name),
  profil:              (name) => name === 'PROFILS_BY_ZONE',
  work_position:       (name) => name === 'PROFILS_BY_ZONE',
};

const selector = (state) => ({
  tables: state.tables,
  currentTemplate: state.currentTemplate,
  sections: state.sections,
});

/** Valeur Ant Design Cascader : alignée sur `formattingCascaderData` / `componentOnChange` */
const getTableCascaderDisplayValue = (box) => {
  if (!box) return null;
  const vp = box.variablePath;
  const hasValidPath = Array.isArray(vp) && vp.length > 0;
  if (hasValidPath) return vp;
  if (!box.tableName) return null;
  if (box.proposal?.type === 'opportunity' && box.proposal?.id != null) {
    return [box.proposal.id, box.tableName];
  }
  return [box.tableName];
};

const TableCascader = ({ onChange, selectedBoxType, selectedBox }) => {
  const { tables, currentTemplate, sections } = useStore(selector);
  const { t } = useTranslation();

  const recursiveModelName = (() => {
    const tplMeta = getSectionRecursiveMeta(currentTemplate);
    if (tplMeta.isRecursive && tplMeta.recursiveModelName) return tplMeta.recursiveModelName;
    if (selectedBox?.fromSectionId) {
      const section = sections?.find(s => String(s.id) === String(selectedBox.fromSectionId));
      const meta = getSectionRecursiveMeta(section);
      if (meta.isRecursive) return meta.recursiveModelName;
    }
    return null;
  })();

  const isTableExcluded = recursiveModelName
    ? (RECURSIVE_MODEL_EXCLUDED_TABLE_PATTERNS[recursiveModelName] ?? (() => false))
    : () => false;

  const formattingCascaderData = (initialData) => {
    let processedData = [];

    const offerTables = Array.isArray(initialData.offer_tables) ? [...initialData.offer_tables] : [];
    const sortedOfferTables = offerTables
      .filter((table) => !isTableExcluded(table))
      .sort((a, b) => t(a).localeCompare(t(b)));

    sortedOfferTables.forEach((table) => {
      processedData.push({
        value: table,
        label: <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <img src={logo} alt="custom" style={{ width: 20, height: 20, }} />
          {t(table)}
        </div>,
        name: t(table),
      });
    });

    initialData.opportunities_table?.forEach((opportunity) => {
      const opportunityTables = Array.isArray(opportunity.opportunity_tables) ? [...opportunity.opportunity_tables] : [];
      const sortedChildren = opportunityTables
        .filter((table) => !isTableExcluded(table))
        .sort((a, b) => t(a).localeCompare(t(b)));

      const children = sortedChildren.map((table) => {
        return {
          key: table,
          value: table,
          label: <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <img src={logo} alt="custom" style={{ width: 20, height: 20, }} />
            {t(table)}
          </div>,
          name: t(table),
        };
      });

      processedData.push({
        key: opportunity.opportunity_id,
        value: opportunity.opportunity_id,
        label: opportunity.opportunity_name,
        children: children,
      });
    });

    if (processedData.length <= 0) {
      const initialDataArray = Array.isArray(initialData) ? [...initialData] : [];
      const sortedInitialData = initialDataArray
        .filter((table) => !isTableExcluded(table))
        .sort((a, b) => t(a).localeCompare(t(b)));

      sortedInitialData.forEach((table) => {
        processedData.push({
          key: table,
          value: table,
          label: <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <img src={logo} alt="custom" style={{ width: 20, height: 20, }} />
            {t(table)}
          </div>,
          name: t(table),
        });
      });
    }

    return processedData;
  };
  // Rendu de la valeur sélectionnée. Une box legacy garde un tableName de variante
  // (ex. BPU_WORK_POSITIONS) que le collapse a retiré de la liste -> antd n'a plus
  // d'option correspondante et affiche la clé brute. Ici on traduit tout segment
  // resté string (= non matché) via t(), tout en conservant les labels riches
  // (JSX logo + nom) des options trouvées.
  const displayRender = (labels) =>
    labels
      .map((label) => (typeof label === 'string' ? t(label) : label))
      .reduce((acc, node, i) => (i ? [...acc, ' / ', node] : [node]), []);

  const componentOnChange = (value) => {
    if (value.length >= 2) {
      onChange(value[1], { id: value[0], type: 'opportunity' });
    } else if (value.length === 1) {
      onChange(value[0]);
    } else {
      onChange(undefined);
    }
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <CascaderCustom
        sytleCustom={{ height: 32, width: '100%' }}
        disableCascader={!isATableBox(selectedBoxType)}
        tablesToDisplay={formattingCascaderData(tables)}
        functionToExec={componentOnChange}
        placeholderToDisplay='Variables Tableaux'
        selectedBox={selectedBox}
        displayValue={getTableCascaderDisplayValue(selectedBox)}
        displayRender={displayRender}
        searchKey='name'
      />
    </div>
  );
};
export default TableCascader;
